import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTranslation } from "@/i18n/use-translation";

import { MenuFields, draftFromMenu, isValid, scheduleBody, type MenuDraft } from "./menu-fields";
import { uploadLine, type Menu } from "./menu-model";
import { useMenuActions } from "./use-menus";

/**
 * A menu's settings: the upload's `MenuFields`, prefilled, saved straight away. Opened from the
 * list's row menu and from the detail page (POS2-1456).
 */
export function MenuSettingsDialog({
  menu,
  menus,
  live,
  today,
  onClose,
  onArchive,
}: {
  menu: Menu;
  menus: Menu[];
  /** Live at the site in view: the dialog then says changes reach Scrappy at once. */
  live: boolean;
  /** The site's today (`useSiteToday`). */
  today: string;
  onClose: () => void;
  onArchive: () => void;
}) {
  const { t, locale } = useTranslation();
  const { saveSettings } = useMenuActions();
  const [draft, setDraft] = useState<MenuDraft>(() => draftFromMenu(t, locale, menu, menus));

  function save() {
    saveSettings.mutate(
      { menuId: menu.id, displayName: draft.name.trim(), schedule: scheduleBody(draft) },
      { onSuccess: onClose },
    );
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("menus.settings.title")}</DialogTitle>
          <DialogDescription>{uploadLine(t, locale, menu)}</DialogDescription>
        </DialogHeader>
        <MenuFields
          draft={draft}
          menus={menus}
          selfId={menu.id}
          today={today}
          onChange={setDraft}
        />
        {live && (
          <p className="rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-500">
            {t("menus.settings.liveNote")}
          </p>
        )}
        <DialogFooter className="sm:justify-between">
          <Button
            variant="ghost"
            size="sm"
            className="text-red-600 hover:text-red-700"
            onClick={onArchive}
          >
            {t("menus.settings.archive")}
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button size="sm" disabled={!isValid(draft) || saveSettings.isPending} onClick={save}>
              {saveSettings.isPending ? t("common.saving") : t("common.save")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
